import React, { useEffect, useState } from 'react';
import api from '../../services/api';

const SERVICE_TYPES = [
  'WOUND_CARE','MEDICATION_ADMINISTRATION','PHYSICAL_THERAPY','OCCUPATIONAL_THERAPY',
  'SPEECH_THERAPY','IV_THERAPY','PALLIATIVE_CARE','POST_OP_CARE','MATERNAL_CARE',
  'PEDIATRIC_CARE','ELDERLY_CARE','MENTAL_HEALTH','CHRONIC_DISEASE_MANAGEMENT','GENERAL_NURSING',
];

export default function RateCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const [form, setForm] = useState({ service_type: '', specialization: '', base_rate_kes: '', per_minute_rate_kes: '', per_km_rate_kes: '', min_billable_minutes: 30, max_billable_minutes: 480 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/billing/config/rate-cards');
      setCards(res.data.data || []);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    // Validate required fields before sending
    if (!editCard && !form.service_type) { alert('Please select a service type.'); return; }
    if (!form.base_rate_kes || Number(form.base_rate_kes) <= 0) { alert('Base rate must be greater than 0.'); return; }
    if (Number(form.min_billable_minutes) >= Number(form.max_billable_minutes)) {
      alert('Max billable minutes must be greater than min billable minutes.'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_rate_kes: Number(form.base_rate_kes),
        per_minute_rate_kes: Number(form.per_minute_rate_kes),
        per_km_rate_kes: Number(form.per_km_rate_kes),
        min_billable_minutes: Number(form.min_billable_minutes),
        max_billable_minutes: Number(form.max_billable_minutes),
      };
      if (editCard) {
        await api.patch(`/billing/config/rate-cards/${editCard.rate_card_id}`, payload);
      } else {
        await api.post('/billing/config/rate-cards', payload);
      }
      setShowForm(false);
      setEditCard(null);
      setForm({ service_type: '', specialization: '', base_rate_kes: '', per_minute_rate_kes: '', per_km_rate_kes: '', min_billable_minutes: 30, max_billable_minutes: 480 });
      await load();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this rate card?')) return;
    try {
      await api.delete(`/billing/config/rate-cards/${id}`);
      await load();
    } catch (e) {
      alert('Deactivate failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const startEdit = (card) => {
    setEditCard(card);
    setForm({ service_type: card.service_type, specialization: card.specialization || '', base_rate_kes: card.base_rate_kes, per_minute_rate_kes: card.per_minute_rate_kes, per_km_rate_kes: card.per_km_rate_kes, min_billable_minutes: card.min_billable_minutes, max_billable_minutes: card.max_billable_minutes });
    setShowForm(true);
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#2b1b22' }}>Rate Card Configuration</h1>
          <p style={{ margin: '4px 0 0', color: '#8b7681', fontSize: 13 }}>Configure service billing rates, per-minute and travel charges</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditCard(null); setForm({ service_type: '', specialization: '', base_rate_kes: '', per_minute_rate_kes: 6, per_km_rate_kes: 25, min_billable_minutes: 30, max_billable_minutes: 480 }); }}
          style={{ background: '#7b1e3c', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          + New Rate Card
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ece3e7', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f5f6' }}>
              {['Service Type', 'Specialization', 'Base Rate', 'Per Min', 'Per Km', 'Min Mins', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#8b7681', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#8b7681' }}>Loading…</td></tr>
            ) : cards.map((c, i) => (
              <tr key={c.rate_card_id} className="hhms-table-row" style={{ borderTop: i === 0 ? 'none' : '1px solid #f0ebed', opacity: c.is_active ? 1 : 0.5 }}>
                <td style={{ padding: '10px 14px', fontWeight: 600 }}>{c.service_type}</td>
                <td style={{ padding: '10px 14px', color: '#555' }}>{c.specialization || 'Any'}</td>
                <td style={{ padding: '10px 14px', fontWeight: 700, color: '#7b1e3c' }}>KES {c.base_rate_kes?.toLocaleString()}</td>
                <td style={{ padding: '10px 14px' }}>KES {c.per_minute_rate_kes}/min</td>
                <td style={{ padding: '10px 14px' }}>KES {c.per_km_rate_kes}/km</td>
                <td style={{ padding: '10px 14px' }}>{c.min_billable_minutes} min</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ background: c.is_active ? '#f0faf4' : '#fdf2f2', color: c.is_active ? '#27ae60' : '#c0392b', borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                    {c.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', display: 'flex', gap: 6 }}>
                  <button onClick={() => startEdit(c)}
                    style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: '#2980b9', fontWeight: 600 }}>Edit</button>
                  {c.is_active && (
                    <button onClick={() => deactivate(c.rate_card_id)}
                      style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: '#c0392b', fontWeight: 600 }}>Off</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 500, maxWidth: '90vw' }}>
            <h3 style={{ margin: '0 0 20px', color: '#2b1b22' }}>{editCard ? 'Edit Rate Card' : 'New Rate Card'}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Service Type</label>
                <select value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }}>
                  <option value="">Select…</option>
                  {SERVICE_TYPES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Specialization (optional)</label>
                <input value={form.specialization} onChange={e => setForm(f => ({ ...f, specialization: e.target.value }))} placeholder="e.g. ICU_NURSE"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Base Rate (KES)</label>
                <input type="number" value={form.base_rate_kes} onChange={e => setForm(f => ({ ...f, base_rate_kes: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Per Minute Rate (KES)</label>
                <input type="number" step="0.01" value={form.per_minute_rate_kes} onChange={e => setForm(f => ({ ...f, per_minute_rate_kes: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Per Km Rate (KES)</label>
                <input type="number" step="0.01" value={form.per_km_rate_kes} onChange={e => setForm(f => ({ ...f, per_km_rate_kes: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Min Billable Minutes</label>
                <input type="number" value={form.min_billable_minutes} onChange={e => setForm(f => ({ ...f, min_billable_minutes: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Max Billable Minutes</label>
                <input type="number" value={form.max_billable_minutes} onChange={e => setForm(f => ({ ...f, max_billable_minutes: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={save} disabled={saving}
                style={{ flex: 1, padding: '10px 0', background: '#7b1e3c', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setShowForm(false)}
                style={{ padding: '10px 20px', background: '#f8f5f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
